import React from 'react';
import { 
  ModemCapabilities,
  STANDARD_CAPABILITIES,
  normalizeCapabilities,
  getCapabilityValue,
  setCapabilityValue,
  CapabilityDefinition
} from '@simcheck/shared-types';
import { Switch, Input, Badge, Label } from '@/components/ui';
import { Wifi, Zap, Radio, Smartphone, Settings, Activity } from 'lucide-react';

interface ModemCapabilitiesEditorProps {
  capabilities: ModemCapabilities | undefined;
  onChange: (capabilities: ModemCapabilities) => void;
  readOnly?: boolean;
}

export const ModemCapabilitiesEditor: React.FC<ModemCapabilitiesEditorProps> = ({
  capabilities,
  onChange,
  readOnly = false
}) => {
  // Normalize capabilities to handle legacy format
  const currentCapabilities = capabilities ? normalizeCapabilities(capabilities) : {};

  const updateCapability = (
    capabilityId: string,
    value: boolean | number | string | string[],
    type: string,
    unit?: string
  ) => {
    if (readOnly) return;

    const updated = setCapabilityValue(
      currentCapabilities,
      capabilityId,
      value,
      type as any,
      unit
    );

    onChange(updated);
  };

  // Parse a free-text "B1, B3 , B20" list into a trimmed, de-duplicated array
  const parseArrayInput = (raw: string): string[] => {
    return Array.from(
      new Set(
        raw
          .split(/[,\n]/)
          .map(s => s.trim())
          .filter(Boolean)
      )
    );
  };

  const getCapabilityBadges = () => {
    const badges: Array<{ label: string; color: string; icon: any }> = [];
    
    // Network generations
    if (getCapabilityValue(currentCapabilities, 'supports5G')) {
      badges.push({ label: '5G', color: 'bg-tech-5g-bg text-tech-5g', icon: Wifi });
    }
    if (getCapabilityValue(currentCapabilities, 'supports4G')) {
      badges.push({ label: '4G LTE', color: 'bg-status-pending-bg text-status-pending', icon: Radio });
    }
    if (getCapabilityValue(currentCapabilities, 'supports3G')) {
      badges.push({ label: '3G', color: 'bg-status-warning-bg text-status-warning', icon: Radio });
    }
    if (getCapabilityValue(currentCapabilities, 'supports2G')) {
      badges.push({ label: '2G', color: 'bg-status-offline-bg text-status-offline', icon: Radio });
    }
    
    // Features
    if (getCapabilityValue(currentCapabilities, 'supportsVoLTE')) {
      badges.push({ label: 'VoLTE', color: 'bg-status-success-bg text-status-success', icon: Smartphone });
    }
    if (getCapabilityValue(currentCapabilities, 'supportsVoNR')) {
      badges.push({ label: 'VoNR', color: 'bg-status-success-bg text-status-success', icon: Smartphone });
    }
    if (getCapabilityValue(currentCapabilities, 'supportseSIM')) {
      badges.push({ label: 'eSIM', color: 'bg-status-pending-bg text-status-pending', icon: Smartphone });
    }
    if (getCapabilityValue(currentCapabilities, 'supportsGPS')) {
      badges.push({ label: 'GPS', color: 'bg-accent-blue-faded text-accent-blue', icon: Smartphone });
    }
    
    // IoT (LPWA)
    if (getCapabilityValue(currentCapabilities, 'supportsLTEM')) {
      badges.push({ label: 'LTE-M', color: 'bg-tech-4g-bg text-tech-4g', icon: Activity });
    }
    if (getCapabilityValue(currentCapabilities, 'supportsNBIoT')) {
      badges.push({ label: 'NB-IoT', color: 'bg-tech-4g-bg text-tech-4g', icon: Activity });
    }

    return badges;
  };

  const getCategoryIcon = (category: string) => {
    switch (category) {
      case 'network': return Wifi;
      case 'iot': return Activity;
      case 'features': return Settings;
      case 'performance': return Zap;
      default: return Radio;
    }
  };

  const getCategoryTitle = (category: string) => {
    switch (category) {
      case 'network': return 'Network Generation Support';
      case 'iot': return 'IoT Technologies';
      case 'features': return 'Advanced Features';
      case 'performance': return 'Performance Specifications';
      case 'other': return 'Certifications & Approvals';
      default: return category;
    }
  };

  const renderCapabilityField = (cap: CapabilityDefinition) => {
    const value = getCapabilityValue(currentCapabilities, cap.id, cap.defaultValue);

    switch (cap.type) {
      case 'boolean':
        return (
          <div key={cap.id} className="flex items-center space-x-2">
            <Switch
              id={cap.id}
              checked={Boolean(value)}
              onCheckedChange={(checked) => updateCapability(cap.id, checked, 'boolean')}
              disabled={readOnly}
            />
            <Label htmlFor={cap.id}>{cap.displayName}</Label>
          </div>
        );

      case 'number':
        return (
          <div key={cap.id} className="space-y-2">
            <label htmlFor={cap.id} className="block text-sm font-medium text-foreground">
              {cap.displayName}
              {cap.unit && <span className="text-muted-foreground/70 ml-1">({cap.unit})</span>}
            </label>
            <Input
              id={cap.id}
              type="number"
              value={value || ''}
              onChange={(e) => updateCapability(cap.id, parseInt(e.target.value) || 0, 'number', cap.unit)}
              placeholder={cap.defaultValue?.toString() || '0'}
              disabled={readOnly}
              min="0"
            />
          </div>
        );

      case 'string':
        return (
          <div key={cap.id} className="space-y-2">
            <label htmlFor={cap.id} className="block text-sm font-medium text-foreground">
              {cap.displayName}
            </label>
            <Input
              id={cap.id}
              type="text"
              value={value || ''}
              onChange={(e) => updateCapability(cap.id, e.target.value, 'string')}
              placeholder={cap.defaultValue?.toString() || ''}
              disabled={readOnly}
            />
          </div>
        );

      case 'enum':
        return (
          <div key={cap.id} className="space-y-2">
            <label htmlFor={cap.id} className="block text-sm font-medium text-foreground">
              {cap.displayName}
            </label>
            <select
              id={cap.id}
              value={value?.toString() || cap.options?.[0] || ''}
              onChange={(e) => updateCapability(cap.id, e.target.value, 'enum')}
              disabled={readOnly}
              className="w-full px-3 py-2 bg-background border border-border rounded-md text-foreground focus:ring-2 focus:ring-accent-blue focus:border-transparent"
            >
              {cap.options?.map(option => (
                <option key={option} value={option}>{option}</option>
              ))}
            </select>
          </div>
        );

      case 'array': {
        const arr: string[] = Array.isArray(value) ? value.map(String) : [];
        const placeholder = cap.options?.slice(0, 3).join(', ') || 'B1, B3, B20';
        return (
          <div key={cap.id} className="space-y-2">
            <label htmlFor={cap.id} className="block text-sm font-medium text-foreground">
              {cap.displayName}
              <span className="text-muted-foreground/70 ml-1 text-xs">(comma-separated)</span>
            </label>
            <Input
              id={cap.id}
              type="text"
              defaultValue={arr.join(', ')}
              onBlur={(e) =>
                updateCapability(cap.id, parseArrayInput(e.target.value), 'array')
              }
              placeholder={placeholder}
              disabled={readOnly}
            />
            {arr.length > 0 && (
              <div className="flex flex-wrap gap-1">
                {arr.map((item) => (
                  <Badge key={item} variant="secondary" className="text-xs">
                    {item}
                  </Badge>
                ))}
              </div>
            )}
            {cap.options && cap.options.length > 0 && (
              <p className="text-xs text-muted-foreground/70">
                Allowed values: {cap.options.join(', ')}
              </p>
            )}
          </div>
        );
      }

      default:
        return null;
    }
  };

  // Group capabilities by category
  const categorizedCapabilities: Record<string, typeof STANDARD_CAPABILITIES> = {};
  STANDARD_CAPABILITIES.forEach(cap => {
    if (!categorizedCapabilities[cap.category]) {
      categorizedCapabilities[cap.category] = [];
    }
    categorizedCapabilities[cap.category].push(cap);
  });

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <Zap className="w-5 h-5" />
        <h3 className="text-lg font-semibold">Modem Capabilities</h3>
      </div>
      
      {/* Capability Badges */}
      <div className="flex flex-wrap gap-1">
        {getCapabilityBadges().map((badge, index) => (
          <Badge key={index} className={`${badge.color} text-xs`}>
            <badge.icon className="w-3 h-3 mr-1" />
            {badge.label}
          </Badge>
        ))}
        {getCapabilityBadges().length === 0 && (
          <span className="text-sm text-muted-foreground/70">
            No capabilities configured
          </span>
        )}
      </div>

      {/* Dynamic capability sections by category */}
      <div className="space-y-6">
        {Object.entries(categorizedCapabilities).map(([category, caps]) => {
          const Icon = getCategoryIcon(category);
          
          return (
            <div key={category}>
              <div className="flex items-center gap-2 mb-3">
                <Icon className="w-4 h-4 text-muted-foreground" />
                <h4 className="text-sm font-medium text-foreground">
                  {getCategoryTitle(category)}
                </h4>
              </div>
              
              {category === 'performance' ? (
                // Grid layout for performance specs
                <div className="grid grid-cols-2 gap-4">
                  {caps.map(cap => renderCapabilityField(cap))}
                </div>
              ) : (
                // Vertical layout for boolean switches
                <div className="space-y-3">
                  {caps.map(cap => renderCapabilityField(cap))}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Help Text */}
      <div className="mt-4 p-3 bg-accent-blue-faded border border-border rounded-md">
        <p className="text-xs text-accent-blue">
          <strong>Note:</strong> Capabilities are automatically populated from the modem models database.
          You can override these values if needed. Changes are saved to the central database.
        </p>
      </div>
    </div>
  );
};
